/**
 * ============================================================================
 * UnivoImage
 * ============================================================================
 *
 * Purpose: Standardized next/image wrapper for the project.
 * Responsibility: Image rendering defaults (priority, sizes, quality, loading).
 * Dependencies: next/image
 * Performance Notes: Centralizes LCP-friendly defaults; avoids raw <img> drift.
 * Impact Area: Adopt incrementally per component.
 * Safe To Modify: Default props and fallback behaviour.
 * Avoid Changing: Prop passthrough contract without auditing usages.
 * Risk Level: Low (opt-in per file)
 * ============================================================================
 */

import Image, { type ImageProps } from "next/image";
import { type CSSProperties } from "react";

export type UnivoImageProps = ImageProps & {
  /** When true, sets fetchPriority high (maps to next/image priority). */
  isLcp?: boolean;
  wrapperClassName?: string;
  wrapperStyle?: CSSProperties;
};

const DEFAULT_QUALITY = 90;
const DEFAULT_SIZES = "100vw";

export default function UnivoImage({
  isLcp = false,
  priority,
  loading,
  quality = DEFAULT_QUALITY,
  sizes = DEFAULT_SIZES,
  wrapperClassName,
  wrapperStyle,
  alt,
  fetchPriority,
  ...props
}: UnivoImageProps) {
  const resolvedPriority = priority ?? isLcp;
  const resolvedLoading = resolvedPriority ? undefined : (loading ?? "lazy");
  const resolvedFetchPriority =
    fetchPriority ?? (resolvedPriority ? "high" : undefined);

  const image = (
    <Image
      {...props}
      alt={alt ?? ""}
      quality={DEFAULT_QUALITY}
      sizes={sizes}
      priority={resolvedPriority}
      loading={resolvedLoading}
      {...(resolvedFetchPriority
        ? { fetchPriority: resolvedFetchPriority }
        : {})}
    />
  );

  if (!wrapperClassName && !wrapperStyle) {
    return image;
  }

  return (
    <span className={wrapperClassName} style={wrapperStyle}>
      {image}
    </span>
  );
}
